"use client";

import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  ExternalLink,
  Laptop,
  Loader2,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Smartphone,
  Trash2,
} from "lucide-react";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { apiDelete, apiGet } from "@/services/api";
import type { Product } from "@/types";
import { CustomerListingForm } from "./customer-listing-form";

interface MyListingsResponse {
  listings: Product[];
}

export function MyListingsView() {
  const [listings, setListings] = useState<Product[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingItem, setEditingItem] = useState<Product | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const res = await apiGet<MyListingsResponse>("/api/marketplace/my-listings");
      setListings(res?.listings ?? []);
    } catch (err) {
      toast.error("Failed to load your listings");
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchListings();
  }, []);

  const handleDelete = async (id: string, title: string) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This cannot be undone.`)) {
      return;
    }
    setDeletingId(id);
    try {
      await apiDelete(`/api/marketplace/listings/${id}`);
      toast.success("Listing deleted successfully");
      setListings((prev) => (prev ? prev.filter((item) => item.id !== id) : []));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete listing");
    } finally {
      setDeletingId(null);
    }
  };

  const counts = useMemo(() => {
    if (!listings) return { total: 0, approved: 0, pending: 0, rejected: 0 };
    return {
      total: listings.length,
      approved: listings.filter((l) => l.status === "APPROVED").length,
      pending: listings.filter((l) => l.status === "PENDING").length,
      rejected: listings.filter((l) => l.status === "REJECTED").length,
    };
  }, [listings]);

  const filteredListings = useMemo(() => {
    if (!listings) return [];
    return listings.filter((item) => {
      const matchesStatus =
        statusFilter === "ALL" ||
        (statusFilter === "APPROVED" && item.status === "APPROVED") ||
        (statusFilter === "PENDING" && item.status === "PENDING") ||
        (statusFilter === "REJECTED" && item.status === "REJECTED");

      const matchesSearch =
        !searchQuery.trim() ||
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.brand?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.sellerPhone?.includes(searchQuery);

      return matchesStatus && matchesSearch;
    });
  }, [listings, statusFilter, searchQuery]);

  // If in create or edit mode, show the customer listing form
  if (isCreating || editingItem) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between border-b border-slate-200 pb-4 dark:border-white/10">
          <button
            type="button"
            onClick={() => {
              setIsCreating(false);
              setEditingItem(null);
            }}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#073B70] dark:text-slate-400 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to my listings
          </button>
          <span className="text-xs font-bold uppercase tracking-wider text-[#FF8A00]">
            {editingItem ? "Edit & Resubmit" : "New Free Listing"}
          </span>
        </div>

        <CustomerListingForm
          isEditing={Boolean(editingItem)}
          initialData={editingItem}
          onSuccess={(savedProduct) => {
            setIsCreating(false);
            setEditingItem(null);
            void fetchListings();
          }}
          onCancel={() => {
            setIsCreating(false);
            setEditingItem(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-white via-slate-50 to-[#087DB5]/5 p-5 shadow-xs dark:border-white/10 dark:bg-slate-900/90 dark:from-slate-900 dark:to-slate-800">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-[#087DB5]/10 px-2.5 py-0.5 text-xs font-bold text-[#087DB5] dark:bg-white/10 dark:text-[#00B7B0]">
                Customer Marketplace
              </span>
              <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                100% Free Posting
              </span>
            </div>
            <h1 className="mt-1 text-2xl font-black text-[#073B70] dark:text-white">
              My Phone &amp; PC Listings
            </h1>
            <p className="mt-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
              Post phones and PCs for sale with your direct phone number. Review status, rejection notes, and update listings anytime.
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void fetchListings()}
              disabled={loading}
              className="gap-1.5"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => setIsCreating(true)}
              className="gap-2 bg-gradient-to-r from-[#FF8A00] to-[#E57A00] text-white shadow-xs hover:from-[#E57A00] hover:to-[#CC6D00]"
            >
              <Plus className="h-4 w-4" />
              Post Free Listing
            </Button>
          </div>
        </div>

        {/* Status Metrics Cards */}
        <div className="mt-5 grid grid-cols-2 gap-2.5 sm:grid-cols-4 sm:gap-3">
          <button
            type="button"
            onClick={() => setStatusFilter("ALL")}
            className={`rounded-xl border p-3 text-left transition-all ${
              statusFilter === "ALL"
                ? "border-[#087DB5] bg-white shadow-xs dark:bg-slate-800"
                : "border-slate-200/80 bg-white/70 hover:border-slate-300 dark:border-white/10 dark:bg-slate-800/50"
            }`}
          >
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Total Listings</p>
            <p className="mt-1 text-xl font-black text-slate-900 dark:text-white">{counts.total}</p>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter("APPROVED")}
            className={`rounded-xl border p-3 text-left transition-all ${
              statusFilter === "APPROVED"
                ? "border-emerald-500 bg-emerald-50/50 shadow-xs dark:bg-emerald-950/20"
                : "border-slate-200/80 bg-white/70 hover:border-emerald-200 dark:border-white/10 dark:bg-slate-800/50"
            }`}
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300">Live / Approved</p>
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            </div>
            <p className="mt-1 text-xl font-black text-emerald-600 dark:text-emerald-400">
              {counts.approved}
            </p>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter("PENDING")}
            className={`rounded-xl border p-3 text-left transition-all ${
              statusFilter === "PENDING"
                ? "border-amber-500 bg-amber-50/50 shadow-xs dark:bg-amber-950/20"
                : "border-slate-200/80 bg-white/70 hover:border-amber-200 dark:border-white/10 dark:bg-slate-800/50"
            }`}
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-300">Pending Review</p>
              <Clock className="h-4 w-4 text-amber-500" />
            </div>
            <p className="mt-1 text-xl font-black text-amber-600 dark:text-amber-400">
              {counts.pending}
            </p>
          </button>

          <button
            type="button"
            onClick={() => setStatusFilter("REJECTED")}
            className={`rounded-xl border p-3 text-left transition-all ${
              statusFilter === "REJECTED"
                ? "border-red-500 bg-red-50/50 shadow-xs dark:bg-red-950/20"
                : "border-slate-200/80 bg-white/70 hover:border-red-200 dark:border-white/10 dark:bg-slate-800/50"
            }`}
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-red-700 dark:text-red-300">Needs Action</p>
              <AlertCircle className="h-4 w-4 text-red-500" />
            </div>
            <p className="mt-1 text-xl font-black text-red-600 dark:text-red-400">
              {counts.rejected}
            </p>
          </button>
        </div>
      </div>

      {/* Filter Tabs and Search Bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1">
          {[
            { key: "ALL", label: "All Listings", count: counts.total },
            { key: "APPROVED", label: "Approved (Live)", count: counts.approved },
            { key: "PENDING", label: "Pending", count: counts.pending },
            { key: "REJECTED", label: "Needs Edit", count: counts.rejected },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setStatusFilter(tab.key)}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold transition-all ${
                statusFilter === tab.key
                  ? "bg-[#073B70] text-white shadow-xs dark:bg-[#087DB5]"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
              }`}
            >
              <span>{tab.label}</span>
              <span
                className={`rounded-full px-1.5 py-0.2 text-[10px] ${
                  statusFilter === tab.key
                    ? "bg-white/20 text-white"
                    : "bg-slate-200 text-slate-700 dark:bg-white/10 dark:text-slate-300"
                }`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        <div className="relative sm:w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by title, brand, phone…"
            className="w-full rounded-xl border border-slate-200 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-900 placeholder-slate-400 shadow-2xs focus:border-[#087DB5] focus:outline-hidden dark:border-white/10 dark:bg-slate-900 dark:text-white"
          />
        </div>
      </div>

      {/* Listings List */}
      {loading ? (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-32 animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-white/5"
            />
          ))}
        </div>
      ) : filteredListings.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center shadow-xs dark:border-white/10 dark:bg-slate-900">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#087DB5]/10 text-[#087DB5] dark:bg-white/10 dark:text-[#00B7B0]">
            <Smartphone className="h-7 w-7" />
          </div>
          <h2 className="mt-4 text-base sm:text-lg font-bold text-slate-900 dark:text-white">
            {listings && listings.length > 0 ? "No listings match your filter" : "No listings posted yet"}
          </h2>
          <p className="mx-auto mt-1 max-w-md text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            {listings && listings.length > 0
              ? "Try changing your status tab or clearing your search query."
              : "Ready to sell an iPhone, Samsung, Dell, HP, or any smartphone / laptop? Post it for free on MOVA Market with your direct contact phone number."}
          </p>
          <div className="mt-5">
            <Button
              type="button"
              variant="primary"
              onClick={() => setIsCreating(true)}
              className="gap-2 bg-gradient-to-r from-[#FF8A00] to-[#E57A00] text-white shadow-xs"
            >
              <Plus className="h-4 w-4" />
              Post Your First Listing
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredListings.map((listing) => {
            const isApproved = listing.status === "APPROVED";
            const isPending = listing.status === "PENDING";
            const isRejected = listing.status === "REJECTED";
            const mainImage =
              listing.images?.[0] ||
              "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=400&auto=format&fit=crop&q=80";

            return (
              <div
                key={listing.id}
                className={`overflow-hidden rounded-2xl border bg-white shadow-xs transition-all dark:bg-slate-900 ${
                  isRejected
                    ? "border-red-300 dark:border-red-900/50"
                    : isPending
                    ? "border-amber-200 dark:border-amber-900/50"
                    : "border-slate-200/90 dark:border-white/10"
                }`}
              >
                <div className="p-4 sm:p-5">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                    {/* Thumbnail */}
                    <div className="relative h-28 w-28 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-100 dark:border-white/10 dark:bg-slate-800">
                      <img
                        src={mainImage}
                        alt={listing.title}
                        className="h-full w-full object-cover"
                      />
                      {listing.category && (
                        <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 py-0.5 text-[9px] font-bold text-white uppercase backdrop-blur-xs">
                          {listing.category.includes("Mobile") ? "Phone" : "PC / Laptop"}
                        </span>
                      )}
                    </div>

                    {/* Main Content */}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {/* Status Badge */}
                        {isApproved && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                            Live on MOVA Market
                          </span>
                        )}
                        {isPending && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                            <Clock className="h-3.5 w-3.5 text-amber-600" />
                            Awaiting Admin Approval
                          </span>
                        )}
                        {isRejected && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-bold text-red-800 dark:bg-red-950/60 dark:text-red-300">
                            <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                            Listing Rejected — Action Required
                          </span>
                        )}

                        {listing.category && (
                          <span className="rounded-md bg-sky-50 dark:bg-sky-950/60 px-2 py-0.5 text-[11px] font-semibold text-[#087DB5] dark:text-[#00B7B0]">
                            {listing.category}
                          </span>
                        )}

                        {listing.brand && (
                          <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700 dark:bg-white/10 dark:text-slate-300">
                            {listing.brand}
                          </span>
                        )}
                      </div>

                      <h3 className="mt-1.5 text-base sm:text-lg font-bold text-slate-900 line-clamp-1 dark:text-white">
                        {listing.title}
                      </h3>

                      <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-slate-600 dark:text-slate-300">
                        <span className="text-base font-black text-[#B12704] dark:text-[#FF8A00]">
                          ETB {Number(listing.price).toLocaleString()}
                        </span>

                        {listing.sellerPhone && (
                          <a
                            href={`tel:${listing.sellerPhone}`}
                            className="inline-flex items-center gap-1.5 font-bold text-[#087DB5] hover:underline"
                          >
                            <Phone className="h-3.5 w-3.5 text-emerald-600" />
                            {listing.sellerPhone}
                          </a>
                        )}

                        <span className="text-slate-400">
                          {listing.updatedAt || listing.createdAt
                            ? `Updated: ${new Date(listing.updatedAt || listing.createdAt!).toLocaleDateString()}`
                            : "Recently added"}
                        </span>
                      </div>

                      {/* Rejection Alert Box */}
                      {isRejected && (
                        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3.5 text-xs text-red-900 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200">
                          <div className="flex items-start gap-2.5">
                            <AlertTriangle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400 mt-0.5" />
                            <div className="min-w-0 flex-1">
                              <p className="font-bold">
                                Reason for Rejection:
                              </p>
                              <p className="mt-0.5 text-slate-700 dark:text-red-200">
                                {listing.rejectionReason || "Listing did not meet marketplace quality guidelines."}
                              </p>
                              <p className="mt-2 font-medium text-red-800 dark:text-red-300">
                                Please click <strong>&quot;Edit &amp; Resubmit&quot;</strong> to fix the items above and resubmit for admin review.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Pending Notice Box */}
                      {isPending && (
                        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/70 p-3 text-xs text-amber-900 dark:border-amber-900/30 dark:bg-amber-950/20 dark:text-amber-200">
                          <p className="flex items-center gap-1.5 font-medium">
                            <Clock className="h-3.5 w-3.5 text-amber-600" />
                            Your listing is in review by our admin team. It will appear on search and category pages once approved.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex shrink-0 items-center gap-2 pt-2 sm:flex-col sm:items-end sm:pt-0">
                      {isRejected ? (
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          onClick={() => setEditingItem(listing)}
                          className="gap-1.5 bg-red-600 hover:bg-red-700 text-white font-bold"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit &amp; Resubmit
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingItem(listing)}
                          className="gap-1.5"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit Listing
                        </Button>
                      )}

                      <div className="flex items-center gap-1">
                        <Link
                          href={`/products/${listing.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
                          title="Preview product page"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          <span>Preview</span>
                        </Link>

                        <button
                          type="button"
                          onClick={() => void handleDelete(listing.id, listing.title)}
                          disabled={deletingId === listing.id}
                          className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-400 transition-colors"
                          title="Delete listing"
                        >
                          {deletingId === listing.id ? (
                            <Loader2 className="h-4 w-4 animate-spin text-red-600" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
